import type { Effect as EffectType } from 'effect';
import { Effect, Option } from 'effect';

import type { PaymentRequestAdapters } from '../../../module/order/orderAdapters';
import { OrderTokenNotFoundError } from '@vs-shop/schemas/domain/paymentRequest';

export type MakeGetPaymentRequestWithOrderDeps<
  PaymentRequest,
  PaymentRequestError,
  PaymentRequestServices,
  Order,
  OrderError,
  OrderServices,
> = {
  readonly getPaymentRequestByHashCheck: (
    hash: string
  ) => EffectType.Effect<PaymentRequest, PaymentRequestError, PaymentRequestServices>;
  readonly getOrderExpanded: (
    tokenValue: string
  ) => EffectType.Effect<Order, OrderError, OrderServices>;
  readonly paymentRequestAdapters: PaymentRequestAdapters<PaymentRequest>;
};

export const makeGetPaymentRequestWithOrder = <
  PaymentRequest,
  PaymentRequestError,
  PaymentRequestServices,
  Order,
  OrderError,
  OrderServices,
>(
  deps: MakeGetPaymentRequestWithOrderDeps<
    PaymentRequest,
    PaymentRequestError,
    PaymentRequestServices,
    Order,
    OrderError,
    OrderServices
  >
) => {
  const getPaymentRequestWithOrder = (hash: string) =>
    Effect.gen(function* () {
      const paymentRequest = yield* deps.getPaymentRequestByHashCheck(hash);

      const orderTokenOption = deps.paymentRequestAdapters.orderToken(paymentRequest);

      if (Option.isNone(orderTokenOption)) {
        return yield* new OrderTokenNotFoundError({ hash });
      }

      const order = yield* deps.getOrderExpanded(orderTokenOption.value);

      return { paymentRequest, order };
    });

  return { getPaymentRequestWithOrder };
};
